// SL.L2 (#2958) — the product backlog: ranked list, drag (and keyboard) to
// reorder, direct add, requirement badges. API: SL.B1 (routes/backlog.ts).
//
// Controls render from the payload's allowedActions only (W1): no role logic
// here. The server's order is the truth: after every reorder, successful or
// not, the list is re-read, so a refused or failed move never stays on
// screen (brief §5).
//
// R1/R2 (#3554): one write at a time (`busy`), so reads can't overlap within
// a project, and a project change remounts the view (BacklogPage's key), so a
// late answer for another project lands on nothing. When a re-read after a
// write fails, the list is marked out of date and reordering stays locked
// until a reload succeeds.
//
// Update round (18 Oct): Plan next sprint reads plan_next from GET /sprints
// (open point B); a SPIKE's outcome (SpikeOutcomeEditor, G1) and an item's
// points (C1's re_estimate) are edited inline, each shown only when the
// item's own allowedActions list it. Both go through writeThenReload, the
// same one-write-at-a-time path as add.
import { useCallback, useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors,
  type Announcements, type DragEndEvent, type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  addBacklogItem, fetchBacklog, fetchSprints, neighboursForMove, reEstimateBacklogItem, recordSpikeOutcome, reorderBacklogItem,
  type BacklogItem, type BacklogItemType, type BacklogPayload, type NewBacklogItem, type SpikeOutcomeBody,
} from '../lib/backlogApi';
import { hasAction } from '../lib/sprintLoopActions';
import PlanNextSprintButton from '../components/PlanNextSprintButton';
import SpikeOutcomeEditor from '../components/SpikeOutcomeEditor';

const STATE_LABELS: Record<string, string> = {
  in_sprint: 'In sprint', done: 'Done', cancelled: 'Cancelled', deferred: 'Deferred',
};

// Direct add (brief §5): technical work and bugs; a US added here is flagged
// "not in approved requirements" by the server.
const ADDABLE_TYPES: { value: BacklogItemType; label: string }[] = [
  { value: 'TECH', label: 'Technical work (TECH)' },
  { value: 'BUG', label: 'Bug (BUG)' },
  { value: 'US', label: 'User story (US), not in approved requirements' },
];

// B1's addItemBody limits (routes/backlog.ts): checked here so the user sees
// why, instead of the server's bare "Validation failed" (R2 P2-3).
const MAX_CRITERIA = 50;
const MAX_CRITERION_LENGTH = 2000;

type Busy = 'reorder' | 'add' | 'item' | null;

/** The edits a row offers; the page runs each write (writeThenReload). */
interface RowEdits {
  saveOutcome: (item: BacklogItem, body: SpikeOutcomeBody) => Promise<void>;
  saveEstimate: (item: BacklogItem, points: number) => Promise<void>;
  blocked: boolean;
}

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

const cardStyle: CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)',
};

const secondaryButton: CSSProperties = {
  padding: '8px 16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)',
  background: 'var(--surface)', color: 'var(--text)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
};

function Badge({ children, tone }: { children: string; tone: 'neutral' | 'warning' | 'error' | 'info' }) {
  const colors = {
    neutral: { bg: 'var(--surface-alt)', fg: 'var(--text-secondary)' },
    info:    { bg: 'rgba(124,58,237,0.10)', fg: '#7c3aed' },
    warning: { bg: 'rgba(245,158,11,0.12)', fg: 'var(--warning, #b45309)' },
    error:   { bg: 'rgba(239,68,68,0.10)', fg: 'var(--error)' },
  }[tone];
  return (
    <span style={{
      display: 'inline-block', fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
      padding: '2px 8px', borderRadius: 999, background: colors.bg, color: colors.fg, whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}

// C1's reEstimateBody: a whole number, 0 to 1000.
const MAX_POINTS = 1000;

/** The item's points; an inline editor when its allowedActions list re_estimate (C1). */
function PointsEditor({ item, onSave, blocked }: {
  item: BacklogItem; onSave: (points: number) => Promise<void>; blocked: boolean;
}) {
  const [value, setValue] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Closing the editor (cancel or save) puts focus back on its trigger (R1 P3).
  const triggerRef = useRef<HTMLButtonElement>(null);
  const refocus = useRef(false);
  useEffect(() => {
    if (value === null && refocus.current) {
      refocus.current = false;
      triggerRef.current?.focus();
    }
  }, [value]);
  const close = () => { refocus.current = true; setValue(null); };
  const shown = item.estimatePoints === null ? 'No estimate' : `${item.estimatePoints} pts`;
  if (!hasAction(item.allowedActions, 're_estimate')) return <Badge tone="neutral">{shown}</Badge>;
  if (value === null) {
    return (
      <button type="button" ref={triggerRef} aria-label={`Re-estimate ${item.key}`} onClick={() => { setError(null); setValue(String(item.estimatePoints ?? '')); }} style={{ ...secondaryButton, padding: '2px 8px', fontSize: 11 }}>
        {shown}
      </button>
    );
  }
  const text = value.trim();
  const valid = /^\d+$/.test(text) && Number(text) <= MAX_POINTS;
  const canSave = valid && !saving && !blocked;
  async function save(e: FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(Number(text));
      close();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }
  const hintId = `points-hint-${item.key}`;
  return (
    <form onSubmit={save} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
      <input
        aria-label={`Points for ${item.key}`} inputMode="numeric" autoFocus value={value}
        aria-invalid={!valid || undefined} aria-describedby={valid ? undefined : hintId}
        onChange={(e) => setValue(e.target.value)} style={{ width: 64, fontSize: 12, padding: '2px 6px' }}
      />
      <button type="submit" disabled={!canSave} style={{ ...secondaryButton, padding: '2px 8px', fontSize: 11 }}>Save estimate</button>
      <button type="button" onClick={close} disabled={saving} style={{ ...secondaryButton, padding: '2px 8px', fontSize: 11 }}>Cancel</button>
      {!valid && <span id={hintId} style={{ fontSize: 12, color: 'var(--error)' }}>A whole number from 0 to {MAX_POINTS}.</span>}
      {error && <span role="alert" style={{ fontSize: 12, color: 'var(--error)' }}>{error}</span>}
    </form>
  );
}

function BacklogRow({ item, position, canReorder, locked, edits }: {
  item: BacklogItem; position: number; canReorder: boolean; locked: boolean; edits: RowEdits;
}) {
  // `disabled` is the guard: dnd-kit then detaches the drag listeners and sets
  // aria-disabled on the handle. The handle is never natively disabled, so a
  // keyboard user's focus survives the drop and the save (R1 P2-1).
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id, disabled: !canReorder || locked });
  const style: CSSProperties = {
    display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)',
    padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--border)',
    background: isDragging ? 'var(--surface-alt)' : 'var(--surface)',
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition, position: 'relative', zIndex: isDragging ? 1 : undefined,
  };
  const stateLabel = STATE_LABELS[item.state];
  return (
    <li ref={setNodeRef} style={style} data-testid="backlog-row" data-item-id={item.id}>
      {canReorder && (
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          aria-label={`Move ${item.key}`}
          style={{
            border: 'none', background: 'transparent', color: 'var(--text-secondary)',
            cursor: locked ? 'not-allowed' : 'grab', fontSize: 16, lineHeight: 1, padding: '2px 4px',
            touchAction: 'none',
          }}
        >
          ⠿
        </button>
      )}
      <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', minWidth: 24, textAlign: 'right' }}>
        {position}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
            {item.key}
          </span>
          {item.type === 'SPIKE' && <Badge tone="info">Spike</Badge>}
          {item.notInRequirements && <Badge tone="warning">Not in approved requirements</Badge>}
          {item.requirementsChanged && <Badge tone="warning">Requirements changed</Badge>}
          {stateLabel && <Badge tone="neutral">{stateLabel}</Badge>}
          <PointsEditor item={item} onSave={(points) => edits.saveEstimate(item, points)} blocked={edits.blocked} />
        </div>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text)', margin: '4px 0 0', overflowWrap: 'anywhere' }}>
          {item.statement}
        </p>
        <SpikeOutcomeEditor item={item} onSave={(body) => edits.saveOutcome(item, body)} blocked={edits.blocked} />
      </div>
    </li>
  );
}

/**
 * The add form only collects the fields. The page sends the POST (onAdd), so
 * a created item always closes the form, even when the re-read after it
 * fails: the form can't be resubmitted into a duplicate (R1/R2 P1-1).
 */
function AddItemForm({ onAdd, onCancel, blocked }: {
  onAdd: (item: NewBacklogItem) => Promise<void>; onCancel: () => void; blocked: boolean;
}) {
  const [type, setType] = useState<BacklogItemType>('TECH');
  const [statement, setStatement] = useState('');
  const [criteria, setCriteria] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const criteriaList = criteria.split('\n').map((c) => c.trim()).filter(Boolean);
  const criteriaProblem = criteriaList.length > MAX_CRITERIA
    ? `Up to ${MAX_CRITERIA} acceptance criteria (this has ${criteriaList.length}).`
    : criteriaList.some((c) => c.length > MAX_CRITERION_LENGTH)
      ? `Each acceptance criterion can be up to ${MAX_CRITERION_LENGTH.toLocaleString('en')} characters.`
      : null;
  const canSubmit = !submitting && !blocked && statement.trim() !== '' && criteriaProblem === null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await onAdd({ type, statement: statement.trim(), acceptanceCriteria: criteriaList });
      // Created: the page has closed this form.
    } catch (err) {
      // Not created: keep the text and say why.
      setError(errorMessage(err));
      setSubmitting(false);
    }
  }

  const fieldStyle: CSSProperties = {
    width: '100%', fontFamily: 'var(--font-body)', fontSize: 14, padding: '8px 10px',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--surface)', color: 'var(--text)',
  };
  const labelStyle: CSSProperties = { display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 };

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={(e) => {
        // Escape inside the open type list closes the list, not the form.
        if (e.key === 'Escape' && !submitting && (e.target as HTMLElement).tagName !== 'SELECT') onCancel();
      }}
      aria-label="Add a backlog item"
      style={{ ...cardStyle, marginBottom: 'var(--space-6)', display: 'grid', gap: 'var(--space-4)' }}
    >
      <div>
        <label htmlFor="backlog-add-type" style={labelStyle}>Type</label>
        <select id="backlog-add-type" value={type} onChange={(e) => setType(e.target.value as BacklogItemType)} style={fieldStyle}>
          {ADDABLE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor="backlog-add-statement" style={labelStyle}>Description</label>
        <textarea
          id="backlog-add-statement" required maxLength={4000} rows={3} autoFocus
          value={statement} onChange={(e) => setStatement(e.target.value)} style={fieldStyle}
        />
      </div>
      <div>
        <label htmlFor="backlog-add-criteria" style={labelStyle}>Acceptance criteria (optional, one per line)</label>
        <textarea
          id="backlog-add-criteria" rows={3} value={criteria} onChange={(e) => setCriteria(e.target.value)} style={fieldStyle}
          aria-describedby={criteriaProblem ? 'backlog-add-criteria-problem' : undefined}
        />
        {criteriaProblem && (
          <p id="backlog-add-criteria-problem" style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', margin: '4px 0 0' }}>
            {criteriaProblem}
          </p>
        )}
      </div>
      {error && <p role="alert" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)', margin: 0 }}>{error}</p>}
      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <button
          type="submit" disabled={!canSubmit}
          style={{
            padding: '8px 16px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--primary)', color: '#fff',
            fontSize: 13, fontWeight: 600, cursor: canSubmit ? 'pointer' : 'not-allowed', opacity: canSubmit ? 1 : 0.7,
          }}
        >
          {submitting ? 'Adding…' : 'Add to backlog'}
        </button>
        <button type="button" onClick={onCancel} disabled={submitting} style={secondaryButton}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** Keyed on the project, so moving to another project's backlog starts fresh (R1/R2 P2). */
export default function BacklogPage() {
  const { projectId } = useParams<{ projectId: string }>();
  return <BacklogView key={projectId} projectId={projectId} />;
}

function BacklogView({ projectId }: { projectId: string | undefined }) {
  const [payload, setPayload] = useState<BacklogPayload | null>(null);
  // What the list shows: the server's order, or a move awaiting its answer.
  const [order, setOrder] = useState<BacklogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  // A re-read after a write failed: the list may not be the server's.
  const [stale, setStale] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [adding, setAdding] = useState(false);
  // GET /sprints' project-level actions: where plan_next is listed (open point B).
  const [sprintActions, setSprintActions] = useState<string[] | undefined>(undefined);
  const [sprintsError, setSprintsError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    const p = await fetchBacklog(projectId);
    if (!Array.isArray(p?.items)) throw new Error('The backlog could not be read.');
    setPayload(p);
    setOrder(p.items);
    setStale(false);
  }, [projectId]);

  useEffect(() => {
    load()
      .catch((e) => setLoadError(errorMessage(e)))
      .finally(() => setLoading(false));
  }, [load]);

  // Separate from the backlog: a failure hides the button and says so, and
  // the list still shows.
  const loadSprintActions = useCallback(async () => {
    if (!projectId) return;
    try {
      const s = await fetchSprints(projectId);
      if (!Array.isArray(s?.allowedActions)) throw new Error('The sprint list could not be read.');
      setSprintActions(s.allowedActions);
      setSprintsError(null);
    } catch (e) {
      setSprintActions(undefined);
      setSprintsError(`Sprint planning could not be read: ${errorMessage(e)}`);
    }
  }, [projectId]);

  useEffect(() => { void loadSprintActions(); }, [loadSprintActions]);

  async function reload() {
    setReloading(true);
    try {
      await load();
      setSaveError(null);
    } catch (e) {
      setSaveError(`The backlog could not be reloaded: ${errorMessage(e)}`);
    } finally {
      setReloading(false);
    }
  }

  async function retryInitialLoad() {
    setLoading(true);
    try {
      await load();
    } catch (e) {
      setLoadError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const canAdd = hasAction(payload?.allowedActions, 'add_item');
  const canReorder = hasAction(payload?.allowedActions, 'reorder');
  const locked = busy !== null || stale || reloading;

  const keyOf = (id: UniqueIdentifier) => order.find((i) => i.id === id)?.key ?? 'item';
  const positionOf = (id: UniqueIdentifier) => order.findIndex((i) => i.id === id) + 1;
  const announcements: Announcements = {
    onDragStart: ({ active }) => `Picked up ${keyOf(active.id)}, position ${positionOf(active.id)} of ${order.length}.`,
    onDragOver: ({ active, over }) => over
      ? `${keyOf(active.id)} is over position ${positionOf(over.id)} of ${order.length}.`
      : `${keyOf(active.id)} is no longer over the list.`,
    onDragEnd: ({ active, over }) => over
      ? `${keyOf(active.id)} dropped at position ${positionOf(over.id)} of ${order.length}. Saving.`
      : `${keyOf(active.id)} dropped. The order is unchanged.`,
    onDragCancel: ({ active }) => `Moving ${keyOf(active.id)} was cancelled. The order is unchanged.`,
  };

  async function handleDragEnd({ active, over }: DragEndEvent) {
    // While `locked`, useSortable's `disabled` stops a drag before it starts,
    // so this handler is never reached then.
    if (!over || !projectId || !payload) return;
    const server = payload.items;
    const ids = server.map((i) => i.id);
    const activeId = String(active.id);
    const neighbours = neighboursForMove(ids, activeId, String(over.id));
    if (!neighbours) return;
    setOrder(arrayMove(server, ids.indexOf(activeId), ids.indexOf(String(over.id))));
    setBusy('reorder');
    setSaveError(null);
    let refusal: string | null = null;
    try {
      await reorderBacklogItem(projectId, activeId, neighbours);
    } catch (e) {
      refusal = errorMessage(e);
    }
    // Saved or not, show the server's order.
    try {
      await load();
      if (refusal) setSaveError(`The new order was not saved: ${refusal}`);
    } catch (e) {
      // The server's order is unknown: lock reordering until a reload works,
      // and say whether the move itself was saved (R1/R2 P2).
      setStale(true);
      if (refusal) {
        setOrder(server);
        setSaveError(`The new order was not saved: ${refusal}. The backlog could not be reloaded either: ${errorMessage(e)}`);
      } else {
        setSaveError(`The move was saved, but the backlog could not be reloaded: ${errorMessage(e)}. The order shown may be out of date.`);
      }
    } finally {
      setBusy(null);
    }
  }

  async function handleAdd(item: NewBacklogItem) {
    if (!projectId) return;
    setBusy('add');
    setSaveError(null);
    let created: BacklogItem;
    try {
      created = await addBacklogItem(projectId, item);
    } catch (e) {
      setBusy(null);
      throw e; // nothing was created: the form keeps the text and shows why
    }
    // Created: close the form whatever happens next, so it can't be resubmitted.
    setAdding(false);
    try {
      await load();
    } catch (e) {
      setStale(true);
      setSaveError(`${created.key} was added, but the backlog could not be reloaded: ${errorMessage(e)}`);
    } finally {
      setBusy(null);
    }
  }

  /**
   * An item write (outcome, estimate) under the one-write lock, then a
   * re-read. A refusal rejects, so the editor stays open with the server's
   * text; once saved, a failed re-read marks the list out of date.
   */
  async function writeThenReload(write: () => Promise<unknown>, saved: string) {
    setBusy('item');
    setSaveError(null);
    try {
      await write();
    } catch (e) {
      setBusy(null);
      throw e;
    }
    try {
      await load();
    } catch (e) {
      setStale(true);
      setSaveError(`${saved}, but the backlog could not be reloaded: ${errorMessage(e)}`);
    } finally {
      setBusy(null);
    }
  }

  const edits: RowEdits = {
    saveOutcome: (item, body) => writeThenReload(() => recordSpikeOutcome(projectId!, item.id, body), `${item.key}'s outcome was saved`),
    saveEstimate: (item, points) => writeThenReload(() => reEstimateBacklogItem(projectId!, item.id, points), `${item.key}'s estimate was saved`),
    blocked: busy !== null || reloading,
  };

  const outOfSync = payload?.outOfSync ?? null;
  const statusText = busy === 'reorder' ? 'Saving the new order…' : busy === 'add' ? 'Adding the item…' : busy === 'item' ? 'Saving…' : reloading ? 'Reloading…' : '';

  return (
    <main style={{ flex: 1, padding: 'var(--space-8)', maxWidth: 1100, margin: '0 auto', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)', flexWrap: 'wrap', marginBottom: 'var(--space-6)' }}>
        <div>
          <Link to={`/projects/${projectId}`} style={{ fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}>
            ← Dashboard
          </Link>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--text)', margin: '4px 0 0' }}>
            Product backlog
          </h1>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            Every item in priority order. The top of the list is planned first.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          {canAdd && !adding && order.length > 0 && (
            <button type="button" onClick={() => setAdding(true)} style={secondaryButton}>
              + Add item
            </button>
          )}
          {projectId && <PlanNextSprintButton projectId={projectId} allowedActions={sprintActions} onPlanned={loadSprintActions} />}
        </div>
      </div>

      {/* Mounted always, so screen readers announce changes to it (R1 P3-4). */}
      <p role="status" aria-live="polite" style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: statusText ? '0 0 var(--space-3)' : 0 }}>
        {statusText}
      </p>

      {sprintsError && (
        <p role="alert" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)', margin: '0 0 var(--space-4)' }}>
          {sprintsError}
        </p>
      )}

      {outOfSync && (
        <div role="alert" style={{ ...cardStyle, marginBottom: 'var(--space-6)', borderColor: 'var(--error)', background: 'rgba(239,68,68,0.05)' }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: 'var(--error)', margin: 0 }}>
            Out of sync with the approved requirements
          </p>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', margin: '4px 0 0' }}>
            The last requirements amendment applied, but the backlog could not be updated from it. These headings failed to re-parse:
          </p>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}>
            {outOfSync.failedHeadings.map((h, i) => <li key={`${i}-${h}`}>{h}</li>)}
          </ul>
        </div>
      )}

      {adding && projectId && (
        <AddItemForm onAdd={handleAdd} onCancel={() => setAdding(false)} blocked={busy === 'reorder' || busy === 'item' || reloading} />
      )}

      {saveError && (
        <p role="alert" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)', margin: '0 0 var(--space-4)' }}>
          {saveError}
        </p>
      )}
      {stale && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', margin: '0 0 var(--space-4)' }}>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
            Reordering is paused until the backlog reloads.
          </span>
          <button type="button" onClick={reload} disabled={reloading || busy !== null} style={secondaryButton}>
            Reload
          </button>
        </div>
      )}

      {loading ? (
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>Loading backlog…</p>
      ) : loadError && !payload ? (
        <div>
          <p role="alert" style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--error)' }}>{loadError}</p>
          <button type="button" onClick={retryInitialLoad} style={secondaryButton}>Try again</button>
        </div>
      ) : order.length === 0 ? (
        !adding && (
          <div style={{ ...cardStyle, textAlign: 'center' }}>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 600, color: 'var(--text)', margin: 0 }}>
              The product backlog is empty
            </p>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', margin: '6px 0 0' }}>
              Approved user stories appear here. Technical work and bugs can also be added directly.
            </p>
            {canAdd ? (
              <button
                type="button" onClick={() => setAdding(true)}
                style={{ marginTop: 'var(--space-4)', padding: '8px 16px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Add the first item
              </button>
            ) : (
              // No role names: add_item is also absent before Phase 3 and on
              // read-only access, so the page can't say who may add (R1 P2-4).
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: '8px 0 0' }}>
                {"Adding items isn't available to you on this backlog."}
              </p>
            )}
          </div>
        )
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            accessibility={{
              announcements,
              screenReaderInstructions: {
                draggable: 'To move an item, focus its handle and press Space or Enter. Use the up and down arrow keys to move it, Space or Enter to drop it, or Escape to cancel.',
              },
            }}
          >
            <SortableContext items={order.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <ol aria-label="Product backlog, in priority order" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {order.map((item, i) => (
                  <BacklogRow key={item.id} item={item} position={i + 1} canReorder={canReorder} locked={locked} edits={edits} />
                ))}
              </ol>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </main>
  );
}
